/**
 * @file OrderDetailPage.tsx
 * @description Карточка заказа: шапка в верхней панели, сведения, полоса
 * «Сейчас», блок «Путь заказа» и вкладки (phase19.24 этап 1, шаг 5.3 —
 * проект `docs/phases/phase19.24-order-card-chain-design.md`; phase19.31 —
 * `CabinetPageHeader`, тихое перечитывание).
 *
 * Страница — раскладка: данные держит `useOrderCard`, цепочку документов —
 * `useOrderChain`, тексты и главную кнопку — `buildOrderNow`. Порядок блоков
 * отвечает на три вопроса менеджера сверху вниз: где заказ, чей ход, что
 * делать мне (§13). Главное действие одно — в верхней панели (SC52), на
 * телефоне каркас переносит его в нижнюю полосу.
 *
 * Индикатор на всю страницу — только при первом открытии (пока заказа нет).
 * После действий во вкладках заказ перечитывается тихо: карточка не
 * пересобирается, вкладка не теряет шторки и прокрутку (замечание
 * пользователя phase19.31 «нижняя часть заказа постоянно перегружает
 * страницу»).
 *
 * На `<lg` «Что дальше» стоит ВЫШЕ пути: с телефона менеджер сначала
 * действует, а разбирается потом.
 */

import { Loader2 } from "lucide-react";
import { useState } from "react";
import { useParams } from "react-router";

import { useHasPermission } from "../../../auth/hooks";
import type { AgreementSendRequest } from "../../../components/orders/delivery/useDeliveryAgreementActions";
import { OrderChainPanel } from "../../../components/orders/chain/OrderChainPanel";
import { buildOrderHeaderMeta } from "../../../components/orders/order-header-meta";
import { OrderCancelDialog } from "../../../components/orders/OrderCancelDialog";
import { OrderCardHeader } from "../../../components/orders/OrderCardHeader";
import { OrderDetailTabs } from "../../../components/orders/OrderDetailTabs";
import { OrderNextStepCard } from "../../../components/orders/OrderNextStepCard";
import { OrderNowStrip } from "../../../components/orders/OrderNowStrip";
import { OrderRefundAlert } from "../../../components/orders/OrderRefundAlert";
import { OrderReviewRequestDialog } from "../../../components/orders/OrderReviewRequestDialog";
import { Alert } from "../../../components/ui/alert";
import { CabinetPageHeader } from "../../../components/ui/CabinetPageHeader";
import { DocumentMetaGrid } from "../../../components/ui/DocumentMetaGrid";
import { useHashScroll } from "../../../hooks/useHashScroll";
import { useOrderCard } from "../../../hooks/useOrderCard";
import { useOrderDelivery } from "../../../hooks/useOrderDelivery";
import { useOrderTabParam } from "../../../hooks/useOrderTabParam";
import type { OrderActionKey } from "../../../lib/order-actions";
import { useOrderChain } from "../../../lib/order-chain";
import { buildOrderNow, orderPrimaryButton } from "../../../lib/order-now";

export function OrderDetailPage() {
  const { slug, id } = useParams<{ slug: string; id: string }>();
  const canManage = useHasPermission("SHOP_MANAGE_ORDERS");
  const canOpenCustomer = useHasPermission("SHOP_VIEW_CUSTOMERS");

  // Путь заказа и доставка — отдельные запросы; любое успешное действие
  // карточки перечитывает и их (дефект QA 2026-09-21, ревью 5.2).
  const chain = useOrderChain(id);
  const delivery = useOrderDelivery(id);
  const card = useOrderCard(id, () => {
    chain.reload();
    void delivery.reload();
  });
  const { tab, focus, setTab } = useOrderTabParam();
  const order = card.order;
  useHashScroll(!!order);
  // Запрос отзыва — действие, а не блок карточки: диалогом владеет страница.
  const [reviewOpen, setReviewOpen] = useState(false);
  const [cancelOpen, setCancelOpen] = useState(false);
  // «Отправить на подтверждение» из «Сейчас»: окно живёт в блоке заявки.
  // Запрос с ключом, а не булево: повторный клик обязан открыть окно снова.
  const [sendAgreement, setSendAgreement] = useState<AgreementSendRequest | null>(null);

  const runAction = (action: OrderActionKey): void => {
    if (action === "requestReview") return setReviewOpen(true);
    if (action === "sendAgreement") {
      // Окно отправки живёт во вкладке «Доставка»: менеджер подтверждает,
      // видя те самые условия (шаг 5.2 §2).
      setTab("delivery");
      setSendAgreement((prev) => ({ key: (prev?.key ?? 0) + 1, data: "send" }));
      return;
    }
    if (action === "agreeDelivery") {
      // Перевод в согласование сразу ведёт туда, где условия выставляют.
      void card.runAction(action).then(() => setTab("delivery"));
      return;
    }
    void card.runAction(action);
  };

  if (!order) {
    return (
      <div className="flex flex-col gap-4">
        <CabinetPageHeader title="Заказ" back={{ to: `/shop/${slug}/orders`, label: "К реестру заказов" }} />
        {card.loading ? (
          <div className="flex justify-center py-12" aria-busy="true" aria-label="Загрузка заказа">
            <Loader2 className="size-6 animate-spin text-on-surface-variant" />
          </div>
        ) : (
          <Alert severity="error">{card.error ?? "Заказ не найден"}</Alert>
        )}
      </div>
    );
  }

  const linkContext = { slug, orderId: order.id };
  const now = buildOrderNow(order, chain.data, card.agreement);
  const primary = orderPrimaryButton(order, now, chain.data, linkContext, canManage);
  const closed = order.stage?.kind === "COMPLETED" || order.stage?.kind === "CANCELLED";
  // Обновление пути и заказа — без размонтирования (вкладки, шторки, прокрутка).
  const refreshAll = () => {
    void card.refresh();
    chain.reload();
  };

  return (
    <div className="flex flex-col gap-4 lg:gap-6" aria-busy={card.refreshing || undefined}>
      <OrderCardHeader
        order={order}
        slug={slug}
        now={now}
        primary={primary}
        canManage={canManage}
        acting={card.acting}
        refreshing={card.refreshing}
        onAction={runAction}
        onCancel={() => setCancelOpen(true)}
      />

      <DocumentMetaGrid items={buildOrderHeaderMeta({ order, slug, canOpenCustomer })} />

      {now ? <OrderNowStrip now={now} /> : null}

      {/* Возврат средств — предупреждение выше остальных алертов (2026-08-14) */}
      <OrderRefundAlert order={order} onOpenPayments={() => setTab("payments")} />

      {/* Путь слева, «Что дальше» справа; на телефоне — наоборот по порядку. */}
      <div className="grid grid-cols-1 gap-4 lg:grid-cols-[minmax(0,1fr)_360px] lg:gap-6">
        <div className="order-2 min-w-0 lg:order-1">
          <OrderChainPanel chain={chain} linkContext={linkContext} closed={closed} />
        </div>
        <div className="order-1 lg:order-2">
          <OrderNextStepCard
            now={now}
            primary={primary}
            slug={slug}
            customerId={order.customerId}
            canManage={canManage}
            onAction={runAction}
          />
        </div>
      </div>

      <OrderDetailTabs
        order={order}
        agreement={card.agreement}
        delivery={delivery}
        canManage={canManage}
        acting={card.acting}
        onAgreeDelivery={() => runAction("agreeDelivery")}
        slug={slug}
        tab={tab}
        focus={focus}
        onTabChange={setTab}
        sendRequest={sendAgreement}
        onSendHandled={() => setSendAgreement(null)}
        // Путь заказа перечитываем вместе с заказом: привязка позиции (OQ-54)
        // и правки состава пересобирают задание — узел «Сборка» обязан это
        // показать без F5.
        onChanged={refreshAll}
      />

      <OrderReviewRequestDialog orderId={order.id} open={reviewOpen} onClose={() => setReviewOpen(false)} />
      <OrderCancelDialog
        open={cancelOpen}
        number={order.number}
        busy={card.acting}
        onClose={() => setCancelOpen(false)}
        onConfirm={card.cancel}
      />
    </div>
  );
}
